import {Animation} from '@PosterWhiteboard/animation/animation.class';
import {AnimationType, getAnimationTime, PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {util} from '@postermywall/fabricjs-2';
import type {SlideItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';

export class IntroAnimation extends Animation {
  public timeForAnimation(time: number): boolean {
    return (time < 0.1 && this.item.onPageStartTime === 0) || (time >= this.item.onPageStartTime && time < this.item.onPageStartTime + 0.1);
  }

  public getAnimatingSlide(): SlideItem {
    if (this.item.isSlideshow()) {
      return this.item.slides.getFirstSlide();
    }
    throw new Error('Invalid item');
  }

  public initAnimation(): void {
    this.onStartValues = this.getOnStartValues();
    this.startingAnimationValues = this.getAnimationValues();
    this.endingAnimationValues = this.defaultValues;
    this.animatingItem.set(this.onStartValues);
    this.animatingItem.set(this.startingAnimationValues);
  }

  public setInitialBlockPositionParams(): void {
    const item = this.getAnimatingItem();
    if (item.isText() || item.isTextSlide()) {
      this.setTextItemBlockOrigin();
    } else if (item.isMediaSlide()) {
      this.setMediaItemBlockOrigin();
    }

    const theta = util.degreesToRadians(this.clipPath.angle);
    const extraDisplacement = 3;

    switch (this.direction) {
      case PositionType.RIGHT:
        this.block.set({
          left: this.clipPath.left + (extraDisplacement + this.clipPath.getScaledWidth()) * Math.cos(theta),
          top: this.clipPath.top + (extraDisplacement + this.clipPath.getScaledWidth()) * Math.sin(theta),
        });
        break;
      case PositionType.LEFT:
        this.block.set({
          left: this.clipPath.left - (extraDisplacement + this.clipPath.getScaledWidth()) * Math.cos(theta),
          top: this.clipPath.top - (extraDisplacement + this.clipPath.getScaledWidth()) * Math.sin(theta),
        });
        break;
      case PositionType.TOP:
        this.block.set({
          left: this.clipPath.left + (extraDisplacement + this.clipPath.getScaledHeight()) * Math.sin(theta),
          top: this.clipPath.top - (extraDisplacement + this.clipPath.getScaledHeight()) * Math.cos(theta),
        });
        break;
      case PositionType.BOTTOM:
        this.block.set({
          left: this.clipPath.left - (extraDisplacement + this.clipPath.getScaledHeight()) * Math.sin(theta),
          top: this.clipPath.top + (extraDisplacement + this.clipPath.getScaledHeight()) * Math.cos(theta),
        });
        break;
    }
  }

  public getAnimationBlockCoordinates(): Record<string, number> {
    const theta = util.degreesToRadians(this.clipPath.angle);
    const extraDisplacement = 3;

    switch (this.direction) {
      case PositionType.RIGHT:
        return {
          left: this.clipPath.left - (extraDisplacement + this.clipPath.getScaledWidth()) * Math.cos(theta),
          top: this.clipPath.top - (extraDisplacement + this.clipPath.getScaledWidth()) * Math.sin(theta),
        };
      case PositionType.LEFT:
        return {
          left: this.clipPath.left + (extraDisplacement + this.clipPath.getScaledWidth()) * Math.cos(theta),
          top: this.clipPath.top + (extraDisplacement + this.clipPath.getScaledWidth()) * Math.sin(theta),
        };
      case PositionType.TOP:
        return {
          left: this.clipPath.left - (extraDisplacement + this.clipPath.getScaledHeight()) * Math.sin(theta),
          top: this.clipPath.top + (extraDisplacement + this.clipPath.getScaledHeight()) * Math.cos(theta),
        };
      case PositionType.BOTTOM:
        return {
          left: this.clipPath.left + (extraDisplacement + this.clipPath.getScaledHeight()) * Math.sin(theta),
          top: this.clipPath.top - (extraDisplacement + this.clipPath.getScaledHeight()) * Math.cos(theta),
        };
      default:
        return {
          left: this.clipPath.left + (extraDisplacement + this.clipPath.getScaledWidth()) * Math.cos(theta),
          top: this.clipPath.top + (extraDisplacement + this.clipPath.getScaledWidth()) * Math.sin(theta),
        };
    }
  }

  public getAnimationBounceCoordinates(): Record<string, number> {
    const bound = this.animatingItem.getBoundingRect();
    switch (this.direction) {
      case PositionType.RIGHT:
        return {
          opacity: 0,
          left: this.animatingItem.left - (bound.left + bound.width) - 10,
        };
      case PositionType.LEFT:
        return {
          opacity: 0,
          left: this.animatingItem.left + (bound.left + bound.width) + 10,
        };
      case PositionType.BOTTOM:
        return {
          opacity: 0,
          top: this.animatingItem.top - (bound.top + bound.height) - 10,
        };
      case PositionType.TOP:
      default:
        return {
          opacity: 0,
          top: this.animatingItem.top + (bound.top + bound.height) + 10,
        };
    }
  }

  public getAnimationPanValues(): Record<string, number> {
    switch (this.direction) {
      case PositionType.RIGHT:
        return {
          opacity: 0,
          left: this.animatingItem.left - 0.2 * this.item.page.poster.width,
        };
      case PositionType.LEFT:
        return {
          opacity: 0,
          left: this.animatingItem.left + 0.2 * this.item.page.poster.width,
        };
      case PositionType.TOP:
        return {
          opacity: 0,
          top: this.animatingItem.top + 70,
        };
      case PositionType.BOTTOM:
      default:
        return {
          opacity: 0,
          top: this.animatingItem.top - 70,
        };
    }
  }

  public getAnimationSlideValues(): Record<string, number> {
    const bound = this.animatingItem.getBoundingRect();
    switch (this.direction) {
      case PositionType.LEFT:
        return {
          left: this.item.page.fabricCanvas.width / this.item.page.poster.scaling.scale - bound.left + this.animatingItem.left,
        };
      case PositionType.RIGHT:
        return {
          left: this.animatingItem.left - (bound.left + bound.width),
        };
      case PositionType.TOP:
        return {
          top: this.item.page.fabricCanvas.height / this.item.page.poster.scaling.scale - bound.top + this.animatingItem.top,
        };
      case PositionType.BOTTOM:
      default:
        return {
          top: this.animatingItem.top - (bound.top + bound.height),
        };
    }
  }

  public getAnimationWipeCoordinates(): Record<string, number> {
    const theta = util.degreesToRadians(this.animatingItem.angle);
    switch (this.direction) {
      case PositionType.BOTTOM:
        return {
          left: this.animatingItem.left + this.animatingItem.getScaledHeight() * Math.sin(theta),
          top: this.animatingItem.top - this.animatingItem.getScaledHeight() * Math.cos(theta),
        };
      case PositionType.TOP:
        return {
          left: this.animatingItem.left - this.animatingItem.getScaledHeight() * Math.sin(theta),
          top: this.animatingItem.top + this.animatingItem.getScaledHeight() * Math.cos(theta),
        };
      case PositionType.LEFT:
        return {
          left: this.animatingItem.left + this.animatingItem.getScaledWidth() * Math.cos(theta),
          top: this.animatingItem.top + this.animatingItem.getScaledWidth() * Math.sin(theta),
        };
      case PositionType.RIGHT:
      default:
        return {
          left: this.animatingItem.left - this.animatingItem.getScaledWidth() * Math.cos(theta),
          top: this.animatingItem.top - this.animatingItem.getScaledWidth() * Math.sin(theta),
        };
    }
  }

  public getAnimationZoomScaleValues(): Record<string, number> {
    switch (this.direction) {
      case PositionType.RIGHT:
        return {
          opacity: 0,
          scaleX: this.animatingItem.scaleX * (this.type === AnimationType.SCALE ? 2 : 1.5),
          scaleY: this.animatingItem.scaleY * (this.type === AnimationType.SCALE ? 2 : 1.5),
        };
      case PositionType.LEFT:
      default:
        return {
          opacity: 0,
          scaleX: this.animatingItem.scaleX / (this.type === AnimationType.SCALE ? 2 : 1.5),
          scaleY: this.animatingItem.scaleY / (this.type === AnimationType.SCALE ? 2 : 1.5),
        };
    }
  }

  public getAnimationPopValues(): Record<string, number> {
    return {
      opacity: 0,
      scaleX: this.animatingItem.scaleX / 4,
      scaleY: this.animatingItem.scaleY / 4,
    };
  }

  public getAnimationPeekValues(): Record<string, number> {
    const theta = util.degreesToRadians(this.animatingItem.angle);
    switch (this.direction) {
      case PositionType.BOTTOM:
        return {
          left: this.animatingItem.left + this.animatingItem.getScaledHeight() * Math.sin(theta),
          top: this.animatingItem.top - this.animatingItem.getScaledHeight() * Math.cos(theta),
        };
      case PositionType.TOP:
        return {
          left: this.animatingItem.left - this.animatingItem.getScaledHeight() * Math.sin(theta),
          top: this.animatingItem.top + this.animatingItem.getScaledHeight() * Math.cos(theta),
        };
      case PositionType.LEFT:
        return {
          left: this.animatingItem.left + this.animatingItem.getScaledWidth() * Math.cos(theta),
          top: this.animatingItem.top + this.animatingItem.getScaledWidth() * Math.sin(theta),
        };
      case PositionType.RIGHT:
      default:
        return {
          left: this.animatingItem.left - this.animatingItem.getScaledWidth() * Math.cos(theta),
          top: this.animatingItem.top - this.animatingItem.getScaledWidth() * Math.sin(theta),
        };
    }
  }

  public getEaseFunctionForPopAndJello(): (timeElapsed: number, startValue: number, byValue: number, duration: number) => number {
    return util.ease.easeOutElastic;
  }

  public getAnimationTimeForEaseFunction(time: number): number {
    return Math.min(Math.max(time - this.item.onPageStartTime, 0), getAnimationTime(this.type, this.speed, this.isTransition));
  }

  public getAnimationTimeForFinalFrame(): number {
    return getAnimationTime(this.type, this.speed, this.isTransition) + this.item.onPageStartTime;
  }

  public isFrameTimeForAnimation(time: number): boolean {
    return time >= this.item.onPageStartTime && time <= this.item.onPageStartTime + getAnimationTime(this.type, this.speed, this.isTransition);
  }

  public updateOpacityOnBlockInit(): void {
    this.getAnimatingItem().fabricObject.set('opacity', 0);
  }

  public blockAnimationOnChange(): void {
    this.animatingItem.setCoords();

    if (this.shouldBlockBeMadeVisible() && this.getAnimatingItem().fabricObject.opacity !== this.viewComponentOpacitySnapshot) {
      this.getAnimatingItem().fabricObject.set('opacity', this.viewComponentOpacitySnapshot);
    }
  }

  public updateOpacityOnBlockEnd(): void {
    this.getAnimatingItem().fabricObject.set('opacity', this.viewComponentOpacitySnapshot);
  }
}
